import React, { useState } from 'react';
import {
  Box,
  Button,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  CircularProgress,
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import RestoreIcon from '@mui/icons-material/Restore';
import { C, T } from './leadOverviewCardTokens';

const restoreButtonSx = {
  textTransform: 'none',
  fontWeight: 800,
  borderRadius: '10px',
  bgcolor: C.red,
  color: '#fff',
  boxShadow: '0 1px 4px rgba(239, 68, 68, 0.45)',
  '&:hover': { bgcolor: C.redDark, boxShadow: '0 2px 12px rgba(153, 27, 27, 0.4)' },
  '&:disabled': { bgcolor: '#FCA5A5', color: '#fff' },
};

export const buildDeletedLeadSubtitle = (leadDetails, latestActivity) => {
  const by = leadDetails?.deleted_by_name;
  const when = leadDetails?.deleted_at_display;
  if (by && when) return `Deleted by ${by} · ${when}`;
  const desc = String(latestActivity?.description || '');
  const m =
    desc.match(/lead\s+deleted\s+by\s+(.+?)\s+on\s+(.+)/i) ||
    desc.match(/deleted\s+by\s+(.+?)\s+on\s+(.+)/i);
  if (m) return `Deleted by ${m[1].trim()} · ${m[2].trim()}`;
  return null;
};

const ageAtDeletionLabel = (days) => {
  if (days == null || Number.isNaN(Number(days))) return null;
  const n = Number(days);
  if (n === 0) return 'Same day';
  if (n === 1) return '1 day';
  return `${n} days`;
};

/**
 * Banner for deleted leads: restore CTA and context (matches lead overview mockup).
 */
const LeadRestoreComponent = ({
  leadDetails,
  leadAgeDays,
  latestActivity,
  onRestore,
  canRecover,
}) => {
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [restoreSubmitting, setRestoreSubmitting] = useState(false);

  const stageBack = leadDetails?.stage_verbose || leadDetails?.stage || 'previous';
  const subtitle = buildDeletedLeadSubtitle(leadDetails, latestActivity);
  const leadIdLabel = leadDetails?.lead_id ? String(leadDetails.lead_id).trim() : '';
  const ageLabel = ageAtDeletionLabel(leadAgeDays);
  const leadDisplayName =
    leadDetails?.name ||
    leadDetails?.customer_name ||
    leadDetails?.full_name ||
    (leadIdLabel ? `Lead ${leadIdLabel}` : 'this lead');

  const openConfirm = () => {
    if (!canRecover || !onRestore) return;
    setConfirmOpen(true);
  };

  const handleCloseConfirm = () => {
    if (restoreSubmitting) return;
    setConfirmOpen(false);
  };

  const handleConfirmRestore = async () => {
    if (!onRestore) return;
    setRestoreSubmitting(true);
    try {
      await Promise.resolve(onRestore());
      setConfirmOpen(false);
    } catch {
      // Errors are typically surfaced by the caller (e.g. toast); user can retry or cancel.
    } finally {
      setRestoreSubmitting(false);
    }
  };

  return (
    <Box
      sx={{
        mb: 2.5,
        borderRadius: '14px',
        overflow: 'hidden',
        bgcolor: C.white,
        border: '1px solid #E5E7EB',
        borderTop: '3px solid #DC2626',
        boxShadow: '0 1px 3px rgba(15, 23, 42, 0.06), 0 8px 24px rgba(15, 23, 42, 0.06)',
      }}
    >
      <Box sx={{ px: { xs: '16px', sm: '22px' }, pt: '20px', pb: '18px' }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            gap: 2,
            flexWrap: 'wrap',
          }}
        >
          <Box sx={{ display: 'flex', gap: 1.75, minWidth: 0, flex: 1 }}>
            <Box
              sx={{
                width: 48,
                height: 48,
                borderRadius: '12px',
                bgcolor: '#FEE2E2',
                border: '1px solid #FECACA',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              <DeleteOutlineIcon sx={{ fontSize: 26, color: C.redDark }} />
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Box
                sx={{
                  display: 'inline-block',
                  px: 1,
                  py: 0.35,
                  mb: '10px',
                  borderRadius: '6px',
                  bgcolor: '#FEF2F2',
                  border: '1px solid #FECACA',
                  fontSize: '10px',
                  fontWeight: 800,
                  letterSpacing: '0.1em',
                  color: C.redDark,
                }}
              >
                DELETED LEAD
              </Box>
              <Typography
                sx={{
                  fontSize: { xs: '16px', sm: '17px' },
                  fontWeight: 900,
                  color: C.slate,
                  letterSpacing: '-0.02em',
                  lineHeight: 1.25,
                  mb: '10px',
                  display: 'block',
                }}
              >
                This lead has been removed from the active pipeline
              </Typography>
              <Box
                sx={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  alignItems: 'center',
                  gap: 1,
                  rowGap: 1,
                }}
              >
                {leadIdLabel ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
                    <Typography sx={{ fontSize: '12px', fontWeight: 600, color: C.slate3 }}>
                      Lead ID
                    </Typography>
                    <Box
                      component="span"
                      sx={{
                        display: 'inline-block',
                        px: 1.25,
                        py: 0.4,
                        borderRadius: '999px',
                        bgcolor: '#F3F4F6',
                        border: '1px solid #E5E7EB',
                        fontSize: '12px',
                        fontWeight: 800,
                        color: C.slate2,
                        letterSpacing: '0.02em',
                      }}
                    >
                      {leadIdLabel}
                    </Box>
                  </Box>
                ) : null}
                {subtitle ? (
                  <Typography
                    component="span"
                    sx={{
                      fontSize: '12px',
                      fontWeight: 500,
                      color: C.slate3,
                      lineHeight: 1.5,
                    }}
                  >
                    {leadIdLabel ? (
                      <Box component="span" sx={{ color: C.slate4, mx: 0.5 }}>
                        ·
                      </Box>
                    ) : null}
                    {subtitle}
                  </Typography>
                ) : !leadIdLabel ? (
                  <Typography sx={{ fontSize: '12px', fontWeight: 500, color: C.slate3, lineHeight: 1.5 }}>
                    It is stored in trash. Restore to edit preferences, log activity, and work the lead again.
                  </Typography>
                ) : null}
              </Box>
              {!subtitle && leadIdLabel ? (
                <Typography sx={{ fontSize: '12px', fontWeight: 500, color: C.slate3, lineHeight: 1.5, mt: 0.75 }}>
                  It is stored in trash. Restore to edit preferences, log activity, and work the lead again.
                </Typography>
              ) : null}
            </Box>
          </Box>
          {ageLabel != null ? (
            <Box
              sx={{
                flexShrink: 0,
                px: 1.75,
                py: 1.25,
                borderRadius: '12px',
                bgcolor: '#FAF6F1',
                border: '1px solid #EDE4D8',
                minWidth: 120,
              }}
            >
              <Typography
                sx={{
                  ...T.innerLabel,
                  textTransform: 'uppercase',
                  letterSpacing: '0.1em',
                  color: C.slate4,
                  mb: '6px',
                  fontWeight: 700,
                  fontSize: '9px',
                }}
              >
                Age at deletion
              </Typography>
              <Typography sx={{ fontSize: '22px', fontWeight: 900, color: C.slate2, lineHeight: 1 }}>
                {ageLabel}
              </Typography>
            </Box>
          ) : null}
        </Box>
      </Box>

      <Box sx={{ height: 1, bgcolor: '#E5E7EB', mx: { xs: '16px', sm: '22px' } }} />

      <Box
        sx={{
          px: { xs: '16px', sm: '22px' },
          py: '18px',
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          alignItems: { xs: 'stretch', sm: 'center' },
          justifyContent: 'space-between',
          gap: 2,
        }}
      >
        <Typography
          sx={{
            fontSize: '12px',
            lineHeight: 1.6,
            color: C.slate3,
            fontWeight: 500,
            maxWidth: 560,
            flex: 1,
            minWidth: 0,
          }}
        >
          <Box component="span" sx={{ fontWeight: 800, color: C.slate2 }}>
            Restoring
          </Box>
          {' '}
          this lead moves it back to the{' '}
          <Box component="span" sx={{ fontWeight: 800, color: C.slate2 }}>{stageBack}</Box>
          {' '}
          stage and returns ownership to the original assignee.
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: { xs: 'stretch', sm: 'flex-end' }, gap: 1 }}>
          {!canRecover ? (
            <Typography sx={{ fontSize: '11px', fontWeight: 600, color: C.slate3, maxWidth: 280, textAlign: { xs: 'left', sm: 'right' } }}>
              Recovery is only available when this lead is marked{' '}
              <Box component="span" sx={{ fontWeight: 800, color: C.slate2 }}>recoverable</Box>.
            </Typography>
          ) : null}
          <Button
            variant="contained"
            size="medium"
            disableElevation
            disabled={!canRecover}
            onClick={openConfirm}
            startIcon={<RestoreIcon sx={{ fontSize: '20px' }} />}
            sx={{
              ...restoreButtonSx,
              py: 1.1,
              px: 2.5,
              fontSize: '14px',
              minHeight: 44,
              alignSelf: { xs: 'stretch', sm: 'flex-end' },
              ...(!canRecover && { opacity: 0.55, cursor: 'not-allowed' }),
            }}
          >
            Restore lead
          </Button>
        </Box>
      </Box>

      <Dialog
        open={confirmOpen}
        onClose={handleCloseConfirm}
        maxWidth="sm"
        fullWidth
        disableEscapeKeyDown={restoreSubmitting}
        PaperProps={{
          sx: {
            borderRadius: '14px',
            border: '1px solid #E5E7EB',
          },
        }}
      >
        <DialogTitle
          sx={{
            fontSize: '18px',
            fontWeight: 800,
            color: C.slate,
            letterSpacing: '-0.02em',
            pb: 1,
          }}
        >
          Restore lead?
        </DialogTitle>
        <DialogContent>
          <DialogContentText
            component="div"
            sx={{ color: C.slate3, fontSize: '14px', lineHeight: 1.6, m: 0 }}
          >
            <Typography sx={{ fontSize: '14px', color: C.slate2, fontWeight: 600, mb: 1.25 }}>
              {leadDisplayName}
            </Typography>
            This will move the lead back to the{' '}
            <Box component="span" sx={{ fontWeight: 800, color: C.slate2 }}>
              {stageBack}
            </Box>{' '}
            stage and return ownership to the original assignee. Continue?
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, pt: 0, gap: 1 }}>
          <Button
            onClick={handleCloseConfirm}
            disabled={restoreSubmitting}
            sx={{ textTransform: 'none', fontWeight: 700, color: C.slate3 }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            disableElevation
            onClick={handleConfirmRestore}
            disabled={restoreSubmitting}
            startIcon={
              restoreSubmitting ? (
                <CircularProgress size={18} color="inherit" />
              ) : (
                <RestoreIcon sx={{ fontSize: '20px' }} />
              )
            }
            sx={{
              ...restoreButtonSx,
              textTransform: 'none',
              minWidth: 160,
            }}
          >
            {restoreSubmitting ? 'Restoring…' : 'Yes, restore'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default LeadRestoreComponent;
